import { useCallback, useEffect, useState } from "react";
import { Archive, Copy, ExternalLink, Search, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { copyText, fmtDate, fmtNum, transferTo, LEAGUE_IMAGES } from "../../lib/publisherTools";

export default function PublisherArchive() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState({});
  const [fLeague, setFLeague] = useState("all");
  const [fMsg, setFMsg] = useState("all");
  const [fMinFol, setFMinFol] = useState("");
  const [fMinDays, setFMinDays] = useState("");
  const [editNote, setEditNote] = useState(null);
  const [noteText, setNoteText] = useState("");
  const navigate = useNavigate();

  const fetchList = useCallback(() => {
    api.get(`/admin/archive?search=${encodeURIComponent(search)}`).then((r) => setItems(r.data)).catch(() => {});
  }, [search]);

  useEffect(() => {
    const t = setTimeout(fetchList, 300);
    return () => clearTimeout(t);
  }, [fetchList]);

  const filtered = items.filter((r) => {
    if (fLeague === "none" && r.league) return false;
    if (["A Ligi", "B Ligi", "C Ligi", "D Ligi"].includes(fLeague) && r.league !== fLeague) return false;
    if (fMsg === "yes" && !r.messaged) return false;
    if (fMsg === "no" && r.messaged) return false;
    if (fMinFol !== "" && (r.follower_count === null || r.follower_count === undefined || r.follower_count < Number(fMinFol))) return false;
    if (fMinDays !== "" && (!r.activity || r.activity.days30 < Number(fMinDays))) return false;
    return true;
  });

  const selItems = filtered.filter((r) => selected[r.id]);
  const copyNames = (list, label) => {
    if (!list.length) { toast.error("Seçim yok"); return; }
    copyText(list.map((r) => r.username).join("\n")).then(() => toast.success(label));
  };
  const deleteSelected = async () => {
    if (!selItems.length) { toast.error("Önce kayıt seçin"); return; }
    if (!window.confirm(`${selItems.length} kayıt arşivden silinsin mi?`)) return;
    try {
      const r = await api.post("/admin/archive/delete", { ids: selItems.map((x) => x.id) });
      toast.success(r.data.message);
      setSelected({});
      fetchList();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const sendSelected = (target) => {
    if (!selItems.length) { toast.error("Önce kayıt seçin"); return; }
    transferTo(selItems.map((r) => r.username));
    toast.success(`${selItems.length} kullanıcı aktarıldı`);
    navigate(target);
  };
  const saveNote = async (id) => {
    try {
      await api.patch(`/admin/archive/${id}`, { note: noteText });
      setEditNote(null);
      fetchList();
      toast.success("Not kaydedildi");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const inputCls = "bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-xs text-white placeholder:text-[#64748B] outline-none focus:border-cyan-400/60 transition-colors";
  const cardCls = "rounded-2xl border border-white/[0.07] bg-[#0A0E17] p-5";
  const btnCls = "inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-3.5 py-1.5 border transition-colors";

  return (
    <div data-testid="archive-page" className="space-y-6">
      <h1 className="font-display font-700 text-lg text-white flex items-center gap-2"><Archive size={18} className="text-cyan-300" /> Yayıncı Arşivi</h1>

      <div className={cardCls}>
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Kullanıcı adı ara..." data-testid="archive-search" className={`${inputCls} pl-8 w-56`} />
          </div>
          <select value={fLeague} onChange={(e) => setFLeague(e.target.value)} data-testid="archive-filter-league" className={inputCls}>
            <option value="all">Lig: Tümü</option>
            <option value="A Ligi">A</option><option value="B Ligi">B</option><option value="C Ligi">C</option><option value="D Ligi">D</option>
            <option value="none">Lig Yok</option>
          </select>
          <input value={fMinFol} onChange={(e) => setFMinFol(e.target.value)} placeholder="Min takipçi" type="number" data-testid="archive-filter-followers" className={`${inputCls} w-28`} />
          <input value={fMinDays} onChange={(e) => setFMinDays(e.target.value)} placeholder="Min 30g yayın" type="number" data-testid="archive-filter-days" className={`${inputCls} w-28`} />
          <select value={fMsg} onChange={(e) => setFMsg(e.target.value)} data-testid="archive-filter-msg" className={inputCls}>
            <option value="all">Mesaj: Tümü</option>
            <option value="yes">Gönderildi</option>
            <option value="no">Gönderilmedi</option>
          </select>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4 p-3 rounded-xl bg-black/30 border border-white/[0.06]">
          <button onClick={() => copyNames(selItems, `${selItems.length} kullanıcı adı kopyalandı.`)} data-testid="archive-copy-selected" className={`${btnCls} text-cyan-300 border-cyan-400/30 hover:bg-cyan-400/10`}><Copy size={12} /> Seçilenleri Kopyala</button>
          <button onClick={() => copyNames(filtered, `${filtered.length} kullanıcı adı kopyalandı.`)} data-testid="archive-copy-all" className={`${btnCls} text-cyan-300 border-cyan-400/30 hover:bg-cyan-400/10`}><Copy size={12} /> Tümünü Kopyala</button>
          <button onClick={() => sendSelected("/admin/mesaj")} data-testid="archive-to-message" className={`${btnCls} text-[#A0ABC0] border-white/10 hover:text-white`}><Send size={12} /> Seçilenleri Toplu Mesaja Gönder</button>
          <button onClick={deleteSelected} data-testid="archive-delete" className={`${btnCls} text-red-300 border-red-400/30 hover:bg-red-500/10`}><Trash2 size={12} /> Arşivden Sil</button>
          <span className="text-[11px] text-[#64748B] ml-auto" data-testid="archive-count">{filtered.length} kayıt · {selItems.length} seçili</span>
        </div>

        {filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/10 py-16 text-center" data-testid="archive-empty">
            <Archive size={28} strokeWidth={1.5} className="text-[#64748B] mx-auto mb-3" />
            <p className="text-[#A0ABC0] text-sm">Arşivde kayıt yok.</p>
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="archive-table">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="text-[10px] text-[#64748B] border-b border-white/[0.07]">
                  <th className="py-2.5 pr-3"><input type="checkbox" checked={filtered.length > 0 && filtered.every((r) => selected[r.id])}
                    onChange={() => setSelected(filtered.every((r) => selected[r.id]) ? {} : Object.fromEntries(filtered.map((r) => [r.id, true])))}
                    data-testid="archive-select-all" className="accent-cyan-400 w-4 h-4" /></th>
                  <th className="py-2.5 pr-3">Profil</th><th className="py-2.5 pr-3">Kullanıcı Adı</th><th className="py-2.5 pr-3">Takipçi</th>
                  <th className="py-2.5 pr-3">Lig</th><th className="py-2.5 pr-3">Son 7 Gün</th><th className="py-2.5 pr-3">Son 30 Gün</th>
                  <th className="py-2.5 pr-3">En Uzun Seri</th><th className="py-2.5 pr-3">Keşfedilme</th><th className="py-2.5 pr-3">Son Kontrol</th>
                  <th className="py-2.5 pr-3">Not</th><th className="py-2.5">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-white/[0.04] hover:bg-white/[0.02]" data-testid={`archive-row-${r.id}`}>
                    <td className="py-2.5 pr-3"><input type="checkbox" checked={!!selected[r.id]} onChange={() => setSelected((s) => ({ ...s, [r.id]: !s[r.id] }))} data-testid={`archive-select-${r.id}`} className="accent-cyan-400 w-4 h-4" /></td>
                    <td className="py-2.5 pr-3"><img src={r.avatar_url || "/logo.png"} alt="" loading="lazy" className="w-10 h-10 rounded-full object-cover bg-[#0F1626]" /></td>
                    <td className="py-2.5 pr-3">
                      <span className="font-semibold text-white">{r.username}</span>
                      {r.messaged && <span className="ml-1.5 text-[8px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-full px-1.5 py-px" data-testid={`archive-messaged-${r.id}`}>Mesaj Gönderildi</span>}
                    </td>
                    <td className="py-2.5 pr-3 text-cyan-300 font-semibold">{r.follower_count !== null && r.follower_count !== undefined ? fmtNum(r.follower_count) : "—"}</td>
                    <td className="py-2.5 pr-3">{r.league ? <span className="inline-flex items-center gap-1.5 text-amber-300 font-semibold"><img src={LEAGUE_IMAGES[r.league]} alt="" className="w-6 h-6" />{r.league}</span> : <span className="text-[#64748B]">—</span>}</td>
                    <td className="py-2.5 pr-3 text-white">{r.activity ? `${r.activity.days7} gün` : "—"}</td>
                    <td className="py-2.5 pr-3 text-white">{r.activity ? `${r.activity.days30} gün` : "—"}</td>
                    <td className="py-2.5 pr-3 text-cyan-300 font-semibold">{r.activity ? `${r.activity.longest_streak} gün` : "—"}</td>
                    <td className="py-2.5 pr-3 text-white">{fmtDate(r.discovered_at)}</td>
                    <td className="py-2.5 pr-3 text-white">{r.checked_at ? fmtDate(r.checked_at) : "—"}</td>
                    <td className="py-2.5 pr-3 max-w-[140px]">
                      {editNote === r.id ? (
                        <span className="flex gap-1">
                          <input value={noteText} onChange={(e) => setNoteText(e.target.value)} data-testid="archive-note-input" className={`${inputCls} w-24`} />
                          <button onClick={() => saveNote(r.id)} data-testid="archive-note-save" className="text-cyan-300 text-[10px] font-semibold">Kaydet</button>
                        </span>
                      ) : (
                        <button onClick={() => { setEditNote(r.id); setNoteText(r.note || ""); }} data-testid={`archive-note-${r.id}`} className="text-[#A0ABC0] hover:text-white text-left truncate block w-full">
                          {r.note || "+ not ekle"}
                        </button>
                      )}
                    </td>
                    <td className="py-2.5">
                      <span className="flex gap-2">
                        <a href={r.profile_url} target="_blank" rel="noreferrer" data-testid={`archive-profile-${r.id}`} className="text-[#A0ABC0] hover:text-cyan-300"><ExternalLink size={13} /></a>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
